import { loadFonts } from 'hooks/useFont';
import isEqual from 'lodash.isequal';
import { RootState, useAppSelector, useAppStore } from 'modules/Editor/store';
import { selectBrandIdx } from 'modules/Editor/store/editorSettingsSelector';
import { selectInfographId } from 'modules/Editor/store/infographSelector';
import { useBrand, useUser } from 'modules/User';
import { BrandOptionDropdown } from 'modules/User/components/BrandOptionDropdown';
import { DocumentFontProvider } from 'modules/common/components/FontFamilySelect/DocumentFonts';
import {
  BRAND_DROPDOWN_CONTAINER_CLASSNAME,
  BRAND_DROPDOWN_TRIGGER_CLASSNAME,
} from 'modules/common/components/FontFamilySelect/SelectList.config';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { CustomFontState } from 'types/customFont.types';

import { addNewDocumentFont, getAllFontsInfograph, getBrandFonts } from './FontProvider.helpers';
import { useLoadBrandFonts } from './useLoadBrandFonts';

const MAX_DOCUMENT_FONTS = 5;

/**
 * Provides the font select component with:
 * 1. Document fonts (from the infograph)
 * 2. The fonts from the selected brand kit
 */
export const FontProvider = ({ children }: { children: ReactNode }) => {
  const brandIdx = useAppSelector(selectBrandIdx);
  const infographId = useAppSelector(selectInfographId);

  const [documentFonts, setDocumentFonts] = useState<string[]>([]);
  // sync the document fonts with the ref - to avoid adding it to the dependency array
  const documentFontsRef = useRef<string[]>([]);
  documentFontsRef.current = documentFonts;

  const store = useAppStore();

  const { isEducationUser } = useUser();
  // Education user should not have any brand features
  const showBrandSection = !isEducationUser;

  const { data: brandData } = useBrand();

  const selectedBrand = brandData?.[brandIdx];
  const selectedBrandFontsRef = useRef<string[]>([]);
  const selectedBrandFonts: string[] = getBrandFonts(selectedBrand);

  // sync the brand fonts with the ref - to avoid adding it to the dependency array
  selectedBrandFontsRef.current = selectedBrandFonts;

  // check if fonts have been extracted from the infograph - should only run once
  const hasAddedInitialDocumentFonts = useRef(false);

  // Custom uploaded fonts dictionary <fontName, fontData> for font loading & displaying font image in the font select
  const customFontsMapRef = useRef(new Map<string, CustomFontState>());

  // filter out brand fonts from the document fonts when the brand is updated / loaded
  const onBrandLoaded = useCallback((newBrandFonts: string[]) => {
    // filter out the brand fonts
    const documentFontsFiltered = documentFontsRef.current.filter(
      (documentFont) => !newBrandFonts.some((brandFont) => brandFont === documentFont),
    );

    // update if the document fonts have changed
    if (!isEqual(documentFontsFiltered, documentFontsRef.current)) {
      setDocumentFonts(documentFontsFiltered);
    }
  }, []);

  // loads the brand fonts when the brand is updated / loaded
  // & populates the customFontsMapRef with the custom fonts
  useLoadBrandFonts({ onBrandLoaded, customFontsMapRef, loadFonts, selectedBrandFonts });

  const addDocumentFont = useCallback((font: string) => {
    if (!font) return;

    // if the font is a brand font it shouldn't be added to the document fonts
    const isBrandFont = selectedBrandFontsRef.current.find((brandFont) => brandFont === font);
    if (isBrandFont) return;

    setDocumentFonts((documentFonts) => addNewDocumentFont(documentFonts, font, MAX_DOCUMENT_FONTS));
  }, []);

  // Set the initial document fonts from the infograph - when the infograph has been loaded
  if (!hasAddedInitialDocumentFonts.current && infographId) {
    const infograph = (store.getState() as RootState).infograph;
    const fonts = getAllFontsInfograph(infograph);

    // filter out the brand fonts (brand fonts might or might not have been loaded yet)
    const fontsWithoutBrand = fonts.filter((font) => !selectedBrandFonts.some((brandFont) => brandFont === font));
    hasAddedInitialDocumentFonts.current = true;
    setDocumentFonts(fontsWithoutBrand.slice(0, MAX_DOCUMENT_FONTS));
  }

  // When the selected brand has changed
  // 1. Load the brand fonts
  // 2. Filter out the brand fonts from the document fonts (if they exist in the document fonts)
  useEffect(() => {
    const selectedBrandFonts = selectedBrandFontsRef.current;

    if (!selectedBrandFonts || !selectedBrandFonts.length) return;
    if (!documentFontsRef.current || !documentFontsRef.current.length) return;

    const infograph = (store.getState() as RootState).infograph;
    const fonts = getAllFontsInfograph(infograph);

    // load the brand fonts
    loadFonts(selectedBrandFonts, customFontsMapRef.current);

    // filter out the brand fonts (brand fonts might or might not have been loaded yet)
    const fontsWithoutBrand = fonts.filter((font) => !selectedBrandFonts.some((brandFont) => brandFont === font));

    // no brand fonts in the document fonts
    if (isEqual(fontsWithoutBrand, documentFontsRef.current)) return;

    setDocumentFonts(fontsWithoutBrand);
  }, [brandIdx, store]);

  return (
    <DocumentFontProvider
      showBrandSection={showBrandSection}
      brandFonts={selectedBrandFonts}
      brandOptions={
        <BrandOptionDropdown
          contentClassName={BRAND_DROPDOWN_CONTAINER_CLASSNAME}
          triggerClassName={BRAND_DROPDOWN_TRIGGER_CLASSNAME}
          triggerProps={{ width: 32 }}
        />
      }
      customFontsMap={customFontsMapRef.current}
      documentFonts={documentFonts}
      addDocumentFont={addDocumentFont}
    >
      {children}
    </DocumentFontProvider>
  );
};
